/**
 * Dispatch toolbar filters grouped into Projects and Scopes sections.
 * Thin wrapper mapping Dispatch state onto the shared {@link FiltersPanel}.
 */
import { useCallback, useContext, useMemo } from "react";

import { PLANNING_TOOLTIP_MESSAGES } from "../../../config";
import { Chip, FiltersPanel } from "../../../components/ui";
import { X } from "../../../components/icons/svg/outline";
import { DispatchContext } from "../../../context/domain/planning/DispatchContext";
import { WeeklyContext } from "../../../context/global/WeeklyContext";
import { suggestDispatchNotesFilterPhrases } from "./dispatchNotesPhraseHelpers";
import { isDispatchVisibleAspect } from "./dispatchFilterModel";

/**
 * Filters button and popover; reads filter state from {@link DispatchContext}
 * and the Show Weekends toggle from {@link WeeklyContext}.
 *
 * @param {void}
 * @returns {JSX.Element}
 */
export default function DispatchFiltersPanel() {
  const { showWeekends, setShowWeekends, scopes } = useContext(WeeklyContext);
  const {
    hideEmptyProjects,
    setHideEmptyProjects,
    filterProjectId,
    setFilterProjectId,
    filterScopeId,
    setFilterScopeId,
    filterWorkerId,
    setFilterWorkerId,
    filterManagerId,
    setFilterManagerId,
    filterStartTime,
    setFilterStartTime,
    filterDetailKind,
    setFilterDetailKind,
    filterHasNotes,
    setFilterHasNotes,
    filterNotesContain,
    setFilterNotesContain,
    dispatchFilterProjectOptions,
    dispatchFilterScopeOptions,
    dispatchFilterWorkerOptions,
    dispatchFilterManagerOptions,
    dispatchFilterStartTimeOptions,
    dispatchFilterDetailKindOptions,
    dispatchFilterActive,
    clearDispatchFilters,
  } = useContext(DispatchContext);

  // Show Weekends lives in WeeklyContext, so it is composed into the
  // active state and clear-all behavior here rather than in the hook.
  const filtersActive = dispatchFilterActive || showWeekends;

  /**
   * Reset every Dispatch filter chip, including Show Weekends, back to defaults.
   *
   * @param {void}
   * @returns {void}
   */
  const clearAllFilters = useCallback(() => {
    setShowWeekends(false);
    clearDispatchFilters();
  }, [setShowWeekends, clearDispatchFilters]);

  /**
   * Top work-relevant phrases from this week's scope notes (Has Notes chips).
   * @param {void}
   * @returns {void}
   */
  const notesPhraseSuggestions = useMemo(
    () =>
      suggestDispatchNotesFilterPhrases(
        (scopes ?? []).filter(isDispatchVisibleAspect),
        { limit: 5 }
      ),
    [scopes]
  );

  /**
   * Apply a suggestion chip to the Notes Contain input (replace current value).
   *
   * @param {string} phrase Suggested phrase from {@link suggestDispatchNotesFilterPhrases}.
   * @returns {void}
   */
  const applyNotesPhraseSuggestion = useCallback(
    (phrase) => {
      setFilterNotesContain(phrase);
    },
    [setFilterNotesContain]
  );

  return (
    <FiltersPanel
      ariaLabel="Dispatch filters"
      iconOnly
      isActive={filtersActive}
      onClearAll={clearAllFilters}
      sections={[
        {
          id: "projects",
          title: "Projects",
          checkboxes: [
            {
              id: "show-empty-projects",
              label: "Show Empty Projects",
              title: PLANNING_TOOLTIP_MESSAGES.showEmptyProjectsDispatch,
              checked: !hideEmptyProjects,
              onChange: (checked) => setHideEmptyProjects(!checked),
            },
            {
              id: "show-weekends",
              label: "Show Weekends",
              checked: showWeekends,
              onChange: setShowWeekends,
            },
          ],
          selects: [
            {
              id: "project",
              label: "Project",
              value: filterProjectId,
              options: dispatchFilterProjectOptions,
              onChange: setFilterProjectId,
              placeholder: "Search projects...",
              emptyPlaceholder: "Search projects...",
            },
            {
              id: "manager",
              label: "Manager",
              value: filterManagerId,
              options: dispatchFilterManagerOptions,
              onChange: setFilterManagerId,
              placeholder: "Search managers...",
              emptyPlaceholder: "Search managers...",
            },
            {
              id: "start-time",
              label: "Start time",
              value: filterStartTime,
              options: dispatchFilterStartTimeOptions,
              onChange: setFilterStartTime,
              placeholder: "Search start times...",
              emptyPlaceholder: "Search start times...",
            },
          ],
        },
        {
          id: "scopes",
          title: "Scopes",
          selects: [
            {
              id: "scope-name",
              label: "Scope name",
              value: filterScopeId,
              options: dispatchFilterScopeOptions,
              onChange: setFilterScopeId,
              placeholder: "Search scopes...",
              emptyPlaceholder: "Search scopes...",
            },
            {
              id: "worker",
              label: "Worker",
              value: filterWorkerId,
              options: dispatchFilterWorkerOptions,
              onChange: setFilterWorkerId,
              placeholder: "Search workers...",
              emptyPlaceholder: "Search workers...",
            },
            {
              id: "details",
              label: "Details",
              value: filterDetailKind,
              options: dispatchFilterDetailKindOptions,
              onChange: setFilterDetailKind,
              placeholder: "Search details...",
              emptyPlaceholder: "Search details...",
            },
          ],
          footerCheckboxes: [
            {
              id: "has-notes",
              label: "Has Notes",
              checked: filterHasNotes,
              onChange: setFilterHasNotes,
              expandContent: (
                <div className="">
                  <div className="relative">
                    <input
                      type="text"
                      value={filterNotesContain}
                      onChange={(event) =>
                        setFilterNotesContain(event.target.value)
                      }
                      placeholder="Filter note text..."
                      className="block w-full rounded-md border border-gray-300 py-1.5 pl-2 pr-8 text-sm text-gray-900 outline-none focus:border-secondary-400 focus:ring-2 focus:ring-secondary-100"
                      aria-label="Notes contain"
                    />
                    {filterNotesContain ? (
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-400 hover:text-gray-600"
                        aria-label="Clear notes filter"
                        onClick={() => setFilterNotesContain("")}
                      >
                        <X className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    ) : null}
                  </div>
                  <label htmlFor="notes-contain" className="text-xxs font-semibold uppercase tracking-wide text-gray-300">Common Phrases</label>
                  {notesPhraseSuggestions.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {notesPhraseSuggestions.map(({ phrase, count }) => (
                        <Chip
                          key={phrase}
                          label={phrase}
                          count={count}
                          onClick={() => applyNotesPhraseSuggestion(phrase)}
                          ariaLabel={`Filter notes containing ${phrase}`}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              ),
            },
          ],
        },
      ]}
    />
  );
}
